import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import Field from 'components/shared/Field';
import FormError from 'components/shared/FormError';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import STEPS from 'constants/steps';
import { clearError, resetPassword, setStep, stopLoading } from 'reduxConfig/auth.creators';
import { validateEmail } from 'helpers/validation';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

function ResetPassword({ error: apiError, dispatch, loading, step }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);

  useEffect(
    () => {
      if (loading) dispatch(stopLoading());
    },
    [] // eslint-disable-line
  );

  const handleChange = e => {
    const { value } = e.target;
    if (apiError && !loading) {
      dispatch(clearError());
    }
    if (emailError) setEmailError(validateEmail(value));
    setEmail(value);
  };

  const handleBlur = e => {
    const { value } = e.target;
    setEmailError(validateEmail(value));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
    } else dispatch(resetPassword(email));
  };

  const resetStep = () => dispatch(setStep(STEPS.RESET));

  const emailSent = step === 'RESET_EMAIL_SENT';
  return (
    <div className={styles.resetWrapper}>
      <SEO title={t('auth_reset_meta_title')} />
      <Heading tag="h1">{t('auth_reset_title')}</Heading>

      {emailSent ? (
        <>
          <Text muted>{t('auth_reset_sent')}</Text>
          <Row styles={[styles.paddedRow]}>
            <ButtonPrimary onClick={resetStep} to={microapps.routes.login.path}>
              {t('auth_reset_login')}
            </ButtonPrimary>
          </Row>
        </>
      ) : (
        <form onSubmit={handleSubmit} autoComplete="on">
          <Text tag="p" muted styles={[styles.belowHeaderText]}>
            <p>{t('auth_reset_prompt')}</p>
            <p>
              {t('auth_change_email_contact_at')} <a href="mailto:support@nbx.com">support@nbx.com</a>
            </p>
          </Text>

          <Field
            id="email"
            name={t('common_email')}
            type="email"
            value={email}
            autoComplete="username"
            error={emailError}
            onBlur={handleBlur}
            onChange={handleChange}
            fieldWrapperStyles={[styles.field]}
          />
          {apiError && <FormError>{t(apiError)}</FormError>}
          <Row>
            <ButtonPrimary
              disabled={!email || emailError || loading}
              loading={loading}
              type="submit"
              fullWidth>
              {t('common_confirm')}
            </ButtonPrimary>
          </Row>
        </form>
      )}
    </div>
  );
}

ResetPassword.propTypes = {
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.string
};

ResetPassword.defaultProps = {
  error: null,
  step: null
};

export default ResetPassword;
